import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Building2,
  Save,
  Loader2,
  Globe,
  Upload,
  AlertCircle,
  Trash2,
  Copy,
  ExternalLink,
  Share2,
  Info
} from 'lucide-react';

// Australian states with electricity emission factors
// NGA Factor Year constant
const NGA_FACTOR_YEAR = 2025;

const australianStates = [
  { code: 'NSW', name: 'New South Wales', factor: 0.64 },
  { code: 'VIC', name: 'Victoria', factor: 0.82 },
  { code: 'QLD', name: 'Queensland', factor: 0.67 },
  { code: 'WA', name: 'Western Australia', factor: 0.51 },
  { code: 'SA', name: 'South Australia', factor: 0.25 },
  { code: 'TAS', name: 'Tasmania', factor: 0.15 },
  { code: 'ACT', name: 'Australian Capital Territory', factor: 0.64 },
  { code: 'NT', name: 'Northern Territory', factor: 0.54 }
];

// Grid regions for WA and NT (different electricity networks)
const gridRegions = {
  'WA': [
    { code: 'WA_SWIS', name: 'South West Interconnected System (Perth, SW WA)', factor: 0.50 },
    { code: 'WA_NWIS', name: 'North West Interconnected System (Pilbara)', factor: 0.56 }
  ],
  'NT': [
    { code: 'NT_DKIS', name: 'Darwin Katherine Interconnected System', factor: 0.56 },
    { code: 'NT_AIUS', name: 'Alice Springs / Tennant Creek (isolated)', factor: 0.56 }
  ]
};

// ASRS Reporting Groups
const asrsGroups = [
  {
    value: 1,
    name: 'Group 1 - Large Entities',
    description: 'First report: FY 2024-25. Reasonable assurance from FY 2029-30.',
    criteria: 'Large listed entities meeting 2 of: >$500M revenue, >$1B assets, >500 employees'
  },
  {
    value: 2,
    name: 'Group 2 - Mid-tier Entities',
    description: 'First report: FY 2026-27. Reasonable assurance from FY 2031-32.',
    criteria: 'Listed entities meeting 2 of: >$200M revenue, >$500M assets, >250 employees'
  },
  {
    value: 3,
    name: 'Group 3 - Smaller Entities',
    description: 'First report: FY 2027-28. Reasonable assurance from FY 2032-33.',
    criteria: 'Other large proprietary companies and registered schemes'
  }
];

// Sector options
const sectorOptions = [
  'Agriculture, Forestry and Fishing',
  'Mining',
  'Manufacturing',
  'Electricity, Gas, Water and Waste Services',
  'Construction',
  'Wholesale Trade',
  'Retail Trade',
  'Accommodation and Food Services',
  'Transport, Postal and Warehousing',
  'Information Media and Telecommunications',
  'Financial and Insurance Services',
  'Rental, Hiring and Real Estate Services',
  'Professional, Scientific and Technical Services',
  'Administrative and Support Services',
  'Public Administration and Safety',
  'Education and Training',
  'Health Care and Social Assistance',
  'Arts and Recreation Services',
  'Other Services'
];

const OrganisationProfilePage = () => {
  const navigate = useNavigate();
  const {
    currentOrg,
    setCurrentOrg,
    selectedYear,
    isAdmin,
    canPerform
  } = useOutletContext();

  useEffect(() => {
    if (currentOrg && !canPerform('SETTINGS_ORG_PROFILE') && !isAdmin) {
      toast.error('Only organization owners and profile admins can access this page');
      navigate('/dashboard');
    }
  }, [currentOrg, canPerform, isAdmin, navigate]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    abn: '',
    state: '',
    electricity_grid_region: '',
    asrs_reporting_group: '',
    sector: '',
    employee_count: '',
    revenue: '',
    website: '',
    reporting_year: ''
  });

  // Load organisation data
  useEffect(() => {
    if (currentOrg) {
      setFormData({
        company_name: currentOrg.name || '',
        abn: currentOrg.abn || '',
        state: currentOrg.state || '',
        electricity_grid_region: currentOrg.electricity_grid_region || '',
        asrs_reporting_group: currentOrg.asrs_reporting_group?.toString() || '',
        sector: currentOrg.sector || '',
        employee_count: currentOrg.employee_count?.toString() || '',
        revenue: currentOrg.revenue?.toString() || '',
        website: currentOrg.website || '',
        reporting_year: currentOrg.reporting_year?.toString() || ''
      });
      setLoading(false);
    }
  }, [currentOrg]);

  // Handle state change - auto-set grid region for WA/NT
  const handleStateChange = (val) => {
    setFormData(prev => ({
      ...prev,
      state: val,
      electricity_grid_region: (val === 'WA') ? 'WA_SWIS' : (val === 'NT') ? 'NT_DKIS' : ''
    }));
  };

  // Toggle public profile
  const togglePublicProfile = async () => {
    if (!currentOrg?.id) return;
    try {
      const newValue = !currentOrg.public_profile_enabled;
      const updateData = {
        public_profile_enabled: newValue,
        updated_at: new Date().toISOString()
      };

      // Generate slug if enabling for first time
      if (newValue && !currentOrg.public_profile_slug) {
        const slug = currentOrg.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '') + '-' + Math.random().toString(36).substring(2, 8);
        updateData.public_profile_slug = slug;
      }

      const { error } = await supabase
        .from('organisations')
        .update(updateData)
        .eq('id', currentOrg.id);

      if (error) throw error;

      if (setCurrentOrg) {
        setCurrentOrg(prev => ({
          ...prev,
          public_profile_enabled: newValue,
          public_profile_slug: updateData.public_profile_slug || prev.public_profile_slug
        }));
      }

      toast.success(newValue ? 'Public profile enabled' : 'Public profile disabled');
    } catch (error) {
      console.error('Error toggling public profile:', error);
      toast.error('Failed to update setting');
    }
  };

  // Toggle show intensity
  const toggleShowIntensity = async () => {
    if (!currentOrg?.id) return;
    try {
      const newValue = !(currentOrg.show_intensity_on_profile ?? true);
      const { error } = await supabase
        .from('organisations')
        .update({
          show_intensity_on_profile: newValue,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrg.id);

      if (error) throw error;

      if (setCurrentOrg) {
        setCurrentOrg(prev => ({ ...prev, show_intensity_on_profile: newValue }));
      }
      toast.success(`Carbon intensity ${newValue ? 'shown' : 'hidden'} on public profile`);
    } catch (error) {
      console.error('Error toggling intensity:', error);
      toast.error('Failed to update setting');
    }
  };

  // Update public profile FY mode
  const updateFYMode = async (value) => {
    if (!currentOrg?.id) return;
    try {
      const updateData = {
        public_profile_fy_mode: value,
        updated_at: new Date().toISOString()
      };
      if (value === 'specific_fy' && !currentOrg?.public_profile_fy_year) {
        updateData.public_profile_fy_year = selectedYear;
      }
      const { error } = await supabase
        .from('organisations')
        .update(updateData)
        .eq('id', currentOrg.id);
      if (error) throw error;
      if (setCurrentOrg) {
        setCurrentOrg(prev => ({
          ...prev,
          public_profile_fy_mode: value,
          public_profile_fy_year: value === 'specific_fy' ? (prev.public_profile_fy_year || selectedYear) : prev.public_profile_fy_year
        }));
      }
      toast.success('Public profile period updated');
    } catch (error) {
      console.error('Error updating FY mode:', error);
      toast.error('Failed to update setting');
    }
  };

  // Update public profile FY year
  const updateFYYear = async (value) => {
    if (!currentOrg?.id) return;
    try {
      const { error } = await supabase
        .from('organisations')
        .update({
          public_profile_fy_year: parseInt(value),
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrg.id);
      if (error) throw error;
      if (setCurrentOrg) {
        setCurrentOrg(prev => ({ ...prev, public_profile_fy_year: parseInt(value) }));
      }
      toast.success(`Public profile will show FY${value - 1}/${value}`);
    } catch (error) {
      console.error('Error updating FY year:', error);
      toast.error('Failed to update setting');
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentOrg?.id) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 2MB.');
      return;
    }

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${currentOrg.id}/logo.${fileExt}`;

      // Delete old logo if exists
      if (currentOrg.logo_path) {
        const oldPath = currentOrg.logo_path.split('/organisation-logos/')[1];
        if (oldPath) {
          await supabase.storage.from('organisation-logos').remove([oldPath]);
        }
      }

      // Upload new logo
      const { error: uploadError } = await supabase.storage
        .from('organisation-logos')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('organisation-logos')
        .getPublicUrl(filePath);

      // Update organisation with new logo path
      await supabase
        .from('organisations')
        .update({ logo_path: publicUrl })
        .eq('id', currentOrg.id);

      if (setCurrentOrg) {
        setCurrentOrg(prev => ({ ...prev, logo_path: publicUrl }));
      }

      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error(error.message || 'Failed to upload logo');
    }
  };

  // Remove logo
  const handleRemoveLogo = async () => {
    if (!currentOrg?.id || !currentOrg?.logo_path) return;
    try {
      const filePath = currentOrg.logo_path.split('/organisation-logos/')[1];
      if (filePath) {
        await supabase.storage.from('organisation-logos').remove([filePath]);
      }

      await supabase
        .from('organisations')
        .update({ logo_path: null })
        .eq('id', currentOrg.id);

      if (setCurrentOrg) {
        setCurrentOrg(prev => ({ ...prev, logo_path: null }));
      }

      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  // Form submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentOrg?.id) return;

    setSaving(true);
    try {
      const gridRegion = (formData.state === 'WA' || formData.state === 'NT')
        ? formData.electricity_grid_region
        : null;

      const { error } = await supabase
        .from('organisations')
        .update({
          name: formData.company_name || 'My Organisation',
          abn: formData.abn || null,
          state: formData.state || null,
          electricity_grid_region: gridRegion,
          asrs_reporting_group: formData.asrs_reporting_group ? parseInt(formData.asrs_reporting_group) : null,
          sector: formData.sector || null,
          employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
          reporting_year: formData.reporting_year ? parseInt(formData.reporting_year) : null,
          revenue: formData.revenue ? parseFloat(formData.revenue) : null,
          website: formData.website || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrg.id);

      if (error) throw error;

      // Update local state
      if (setCurrentOrg) {
        setCurrentOrg(prev => ({
          ...prev,
          name: formData.company_name || 'My Organisation',
          abn: formData.abn || null,
          state: formData.state || null,
          electricity_grid_region: gridRegion,
          asrs_reporting_group: formData.asrs_reporting_group ? parseInt(formData.asrs_reporting_group) : null,
          sector: formData.sector || null,
          employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
          reporting_year: formData.reporting_year ? parseInt(formData.reporting_year) : null,
          revenue: formData.revenue ? parseFloat(formData.revenue) : null,
          website: formData.website || null
        }));
      }

      toast.success('Organisation settings saved!');
    } catch (error) {
      console.error('Error saving organisation:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Delete organisation
  const handleDeleteOrganisation = async () => {
    if (!currentOrg?.id) return;

    const confirmText = prompt('Type DELETE to confirm deletion of this organisation:');
    if (confirmText !== 'DELETE') {
      if (confirmText !== null) {
        toast.error('Confirmation text did not match. Organisation not deleted.');
      }
      return;
    }

    try {
      const { error } = await supabase
        .from('organisations')
        .delete()
        .eq('id', currentOrg.id);

      if (error) throw error;

      toast.success('Organisation deleted');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting organisation:', error);
      toast.error('Failed to delete organisation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-56px)] bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]">
      {/* Page Header */}
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Building2 className="w-7 h-7 mr-3 text-[#74A57F]" />
              Organisation Settings
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Manage your company profile and public page
            </p>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-[#003B5C] hover:bg-[#002A42]"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 md:px-6 pb-8">
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Logo Upload Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <Label className="text-[#003B5C] font-medium">Business Logo</Label>
            <p className="text-xs text-slate-500 mb-3">Recommended: 400x100px, transparent PNG or SVG</p>
            <div className="flex items-center space-x-4">
              <div className="w-24 h-16 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden">
                {currentOrg?.logo_path ? (
                  <img
                    src={currentOrg.logo_path}
                    alt="Organisation logo"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <Building2 className="w-6 h-6 text-slate-300" />
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/png,image/svg+xml,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
                <label
                  htmlFor="logo-upload"
                  className="inline-flex items-center px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-lg cursor-pointer transition-colors"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Logo
                </label>
                {currentOrg?.logo_path && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="ml-2 text-xs text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <div>
              <Label className="text-[#003B5C]">Organisation Name</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Your company name"
                className="mt-1"
                data-testid="profile-company-name"
              />
            </div>

            <div>
              <Label className="text-[#003B5C]">ABN (Australian Business Number)</Label>
              <Input
                value={formData.abn}
                onChange={(e) => setFormData({ ...formData, abn: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                placeholder="12 345 678 901"
                className="mt-1"
                data-testid="profile-abn"
              />
              <p className="text-xs text-slate-500 mt-1">11 digits, no spaces</p>
            </div>

            <div>
              <Label className="text-[#003B5C]">State/Territory *</Label>
              <Select
                value={formData.state}
                onValueChange={handleStateChange}
              >
                <SelectTrigger className="mt-1" data-testid="profile-state">
                  <SelectValue placeholder="Select your state" />
                </SelectTrigger>
                <SelectContent>
                  {australianStates.map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Show NGA factor info when state is selected */}
              {formData.state && (
                <div className="mt-2 flex items-center text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                  <Info className="w-3.5 h-3.5 mr-2 text-[#74A57F] flex-shrink-0" />
                  <span>
                    <span className="font-medium text-[#003B5C]">
                      {australianStates.find(s => s.code === formData.state)?.name}
                    </span>
                    {' '}electricity factor:{' '}
                    <span className="font-semibold text-[#74A57F]">
                      {australianStates.find(s => s.code === formData.state)?.factor} kg CO₂-e/kWh
                    </span>
                    {' '}
                    <span className="text-slate-400">(NGA {NGA_FACTOR_YEAR})</span>
                  </span>
                </div>
              )}
              {!formData.state && (
                <p className="text-xs text-slate-500 mt-1">State determines electricity emission factor</p>
              )}
            </div>

            {/* Grid Region Selector - Only show for WA and NT */}
            {(formData.state === 'WA' || formData.state === 'NT') && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <Label className="text-[#003B5C] font-medium">Electricity Grid Region *</Label>
                <p className="text-xs text-amber-700 mb-2">
                  {formData.state === 'WA'
                    ? 'Western Australia has two separate electricity grids with different emission factors.'
                    : 'Northern Territory has multiple isolated grids with different emission factors.'
                  }
                </p>
                <Select
                  value={formData.electricity_grid_region}
                  onValueChange={(val) => setFormData({ ...formData, electricity_grid_region: val })}
                >
                  <SelectTrigger className="mt-1 bg-white" data-testid="profile-grid-region">
                    <SelectValue placeholder="Select your electricity grid" />
                  </SelectTrigger>
                  <SelectContent>
                    {gridRegions[formData.state]?.map((grid) => (
                      <SelectItem key={grid.code} value={grid.code}>
                        <div className="flex flex-col">
                          <span>{grid.name}</span>
                          <span className="text-xs text-slate-500">Factor: {grid.factor} kg CO₂-e/kWh</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* ASRS Reporting Group */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <Label className="text-[#003B5C] font-medium">ASRS Reporting Group</Label>
            <p className="text-xs text-blue-700 mb-2">
              Australian Sustainability Reporting Standards classification. Determines your disclosure timeline and assurance requirements.
            </p>
            <Select
              value={formData.asrs_reporting_group || 'none'}
              onValueChange={(val) => setFormData({ ...formData, asrs_reporting_group: val === 'none' ? '' : val })}
            >
              <SelectTrigger className="mt-1 bg-white" data-testid="profile-asrs-group">
                <SelectValue placeholder="Select your ASRS group (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified / Not applicable</SelectItem>
                {asrsGroups.map((group) => (
                  <SelectItem key={group.value} value={group.value.toString()}>
                    <div className="flex flex-col py-1">
                      <span className="font-medium">{group.name}</span>
                      <span className="text-xs text-slate-500">{group.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.asrs_reporting_group && formData.asrs_reporting_group !== 'none' && (
              <p className="text-xs text-blue-600 mt-2">
                <strong>Criteria:</strong> {asrsGroups.find(g => g.value === parseInt(formData.asrs_reporting_group))?.criteria}
              </p>
            )}
          </div>

          {/* Business Details */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <div>
              <Label className="text-[#003B5C]">Sector</Label>
              <Select
                value={formData.sector}
                onValueChange={(val) => setFormData({ ...formData, sector: val })}
              >
                <SelectTrigger className="mt-1" data-testid="profile-sector">
                  <SelectValue placeholder="Select your industry sector" />
                </SelectTrigger>
                <SelectContent>
                  {sectorOptions.map((sector) => (
                    <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#003B5C]">Employee Count (FTE)</Label>
              <Input
                type="number"
                value={formData.employee_count}
                onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })}
                placeholder="Number of full-time employees"
                className="mt-1"
                data-testid="profile-employees"
              />
            </div>

            <div>
              <Label className="text-[#003B5C]">Annual Revenue (AUD)</Label>
              <Input
                type="number"
                value={formData.revenue}
                onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                placeholder="Annual revenue for intensity calculations"
                className="mt-1"
                data-testid="profile-revenue"
              />
              <p className="text-xs text-slate-500 mt-1">Used to calculate emission intensity metrics</p>
            </div>

            <div>
              <Label className="text-[#003B5C]">Website URL</Label>
              <Input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://www.example.com"
                className="mt-1"
                data-testid="profile-website"
              />
              <p className="text-xs text-slate-500 mt-1">Used in AI-generated report content</p>
            </div>

          </div>

          {/* Public Sustainability Profile */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-medium text-[#003B5C] flex items-center">
                  <Globe className="w-4 h-4 mr-2" />
                  Public Sustainability Profile
                </h4>
                <p className="text-xs text-slate-500">Share your emissions data publicly</p>
              </div>
              <button
                type="button"
                onClick={togglePublicProfile}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${currentOrg?.public_profile_enabled ? 'bg-[#74A57F]' : 'bg-slate-200'
                  }`}
                data-testid="public-profile-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${currentOrg?.public_profile_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>

            {currentOrg?.public_profile_enabled && currentOrg?.public_profile_slug && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-2">Your public profile URL:</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/profile/${currentOrg.public_profile_slug}`}
                    className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1.5 text-slate-600"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/profile/${currentOrg.public_profile_slug}`);
                      toast.success('Link copied!');
                    }}
                    className="text-xs"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                  <a
                    href={`/profile/${currentOrg.public_profile_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button type="button" size="sm" variant="outline" className="text-xs">
                      <Share2 className="w-3 h-3" />
                    </Button>
                  </a>
                </div>

                {/* Show Intensity Toggle */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
                  <div>
                    <p className="text-xs font-medium text-[#003B5C]">Show Carbon Intensity</p>
                    <p className="text-xs text-slate-400">Display tCO₂-e per employee on public page</p>
                  </div>
                  <button
                    type="button"
                    onClick={toggleShowIntensity}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${(currentOrg?.show_intensity_on_profile ?? true) ? 'bg-[#74A57F]' : 'bg-slate-200'
                      }`}
                    data-testid="show-intensity-toggle"
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${(currentOrg?.show_intensity_on_profile ?? true) ? 'translate-x-5' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>

                {/* Public Profile FY Selection */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
                  <div>
                    <p className="text-xs font-medium text-[#003B5C]">Emissions Period</p>
                    <p className="text-xs text-slate-400">Choose which period to display</p>
                  </div>
                  <Select
                    value={currentOrg?.public_profile_fy_mode || 'all_time'}
                    onValueChange={updateFYMode}
                  >
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_time">All Time</SelectItem>
                      <SelectItem value="specific_fy">Specific FY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* FY Year Selector */}
                {currentOrg?.public_profile_fy_mode === 'specific_fy' && (
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <p className="text-xs font-medium text-[#003B5C]">Financial Year</p>
                      <p className="text-xs text-slate-400">Select which FY to display</p>
                    </div>
                    <Select
                      value={(currentOrg?.public_profile_fy_year || selectedYear || new Date().getFullYear()).toString()}
                      onValueChange={updateFYYear}
                    >
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(7)].map((_, i) => {
                          const year = new Date().getFullYear() + 1 - i;
                          return (
                            <SelectItem key={year} value={year.toString()}>
                              FY{year - 1}/{year}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="bg-white rounded-xl border border-red-200 p-6">
            <h4 className="text-red-600 font-semibold mb-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              Danger Zone
            </h4>
            <p className="text-sm text-slate-600 mb-3">
              Permanently delete this organisation and all associated data. This action cannot be undone.
            </p>
            <Button
              type="button"
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              onClick={handleDeleteOrganisation}
              data-testid="delete-org-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Organisation
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganisationProfilePage;
